import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import { getUserRole } from '../utils/rolePermissions';
import { useModal } from '../context/ModalContext';
import { useOrgBrand } from '../context/OrgBrandContext';
import RecipientSelectorModal from './RecipientSelectorModal';
import CommentModal from './CommentModal';
import OperatorImageModal from './OperatorImageModal';
import MonthlyProgressTracker from './MonthlyProgressTracker';
import { useSortableTable, sortData } from '../hooks/useSortableTable';
import { getUsableOriginalUrl } from '../utils/imageUrl';
import { formatBlogBody } from '../utils/blogFormatting';
import '../styles/ReviewContentPanel.css';
import '../styles/ClientDashboard.css';
import '../styles/OperatorImageModal.css';
import CustomSelect from './CustomSelect';

const ReviewContentPanel = ({ user, isProxyMode = false }) => {
  const modal = useModal();
  const { selectedBrand, selectedOrganization } = useOrgBrand();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState(() => {
    // Check if navigation from to-do list forced a specific tab
    const forcedTab = sessionStorage.getItem('force_review_content_tab');
    if (forcedTab) {
      sessionStorage.removeItem('force_review_content_tab');
      return forcedTab;
    }
    return localStorage.getItem('review_content_active_tab') || 'current';
  });

  useEffect(() => {
    localStorage.setItem('review_content_active_tab', activeTab);
  }, [activeTab]);

  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Pagination state for history
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_ITEMS_PER_PAGE = 50;
  const [selectedDrafts, setSelectedDrafts] = useState({});  // neutral (checkbox)
  const [approvedDrafts, setApprovedDrafts] = useState({});  // ✓ staged for send
  const [discardedDrafts, setDiscardedDrafts] = useState({}); // ✗ staged for discard
  const [sendingBatch, setSendingBatch] = useState(false);
  const [uploadingDraft, setUploadingDraft] = useState(null);
  const [recipientModal, setRecipientModal] = useState({ open: false, brandId: null, draftIds: [] });
  // Bumped after a Green Light instant-approval so MonthlyProgressTracker
  // re-fetches immediately instead of waiting for the next brand change.
  const [progressRefreshKey, setProgressRefreshKey] = useState(0);

  // Edit modal
  const [editModal, setEditModal] = useState({ open: false, draft: null });
  const [editForm, setEditForm] = useState({ headline: '', body: '', hashtags: [] });
  // Snapshot of editForm as loaded, so Save can stay disabled until something
  // actually differs - opening and re-saving untouched must not count as an edit.
  const [editFormOriginal, setEditFormOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [clearingFlag, setClearingFlag] = useState(false);

  // View-only, side-by-side source viewer within the Edit Content Draft modal -
  // operators can compare a (possibly flagged) draft against exactly what it was
  // generated from, same pattern as the Edit Idea modal in IdeasPanel.js. No
  // add/remove here - sources on a content draft are a fixed record of what the
  // draft was grounded on, not something to edit after the fact.
  const [sourceViewer, setSourceViewer] = useState({ open: false, loading: false, source: null, copied: false });

  // View-only pane for the idea a draft was approved from - closed by default,
  // opened on demand the same way a source is (not shown automatically on open).
  // Only drafts created from an approved idea (idea_id + ideas_doc_id) have one;
  // operator-created drafts (manual "Create Content" flow) don't.
  const [ideaViewer, setIdeaViewer] = useState({ open: false, loading: false, idea: null });

  // Fallback source stubs resolved live from the approved idea, used only when
  // the draft's own stored `sources` snapshot is empty (e.g. a draft saved before
  // that snapshot was captured correctly) - keeps "Source" visible for every
  // idea-originated draft instead of just the ones with a populated snapshot.
  const [ideaSourceFallback, setIdeaSourceFallback] = useState([]);

  // Comment modal
  const [commentModalState, setCommentModalState] = useState({ open: false, draftId: null, comments: [] });

  // View/Preview modal state
  const [viewingDraft, setViewingDraft] = useState(null);
  const [previewImageDimensions, setPreviewImageDimensions] = useState(null);

  // Operator image modal state
  const [operatorImageModal, setOperatorImageModal] = useState({
    open: false,
    draft: null
  });

  // Extract the actual IDs we use for filtering
  const brandId = selectedBrand && selectedBrand !== 'all' ? (selectedBrand._id || selectedBrand.id) : null;
  const orgId = selectedOrganization && selectedOrganization !== 'all' ? (selectedOrganization._id || selectedOrganization.id) : null;

  useEffect(() => {
    fetchDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, orgId]);

  useEffect(() => {
    if (activeTab === 'history') {
      setHistoryPage(1); // Reset to page 1 when switching to history
      fetchActivityLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, brandId, orgId]);

  useEffect(() => {
    const usableOriginal = viewingDraft && getUsableOriginalUrl(viewingDraft);
    if (usableOriginal) {
      const img = new Image();
      img.onload = () => {
        setPreviewImageDimensions({ width: img.width, height: img.height });
      };
      img.src = usableOriginal;
    } else {
      setPreviewImageDimensions(null);
    }
  }, [viewingDraft]);

  const [historyDraftsSortConfig, setHistoryDraftsSortConfig] = useState({ key: 'updated_at', direction: 'desc' });
  const [opDraftsSortConfig, setOpDraftsSortConfig] = useState({ key: 'actionDate', direction: 'desc' });

  const fetchDrafts = async () => {
    try {
      setLoading(true);

      // Build query parameters based on selected brand/org
      const params = {};
      if (selectedBrand && selectedBrand !== 'all') {
        const brandId = selectedBrand._id || selectedBrand.id;
        params.brand_id = brandId;
      }
      if (selectedOrganization && selectedOrganization !== 'all') {
        const orgId = selectedOrganization._id || selectedOrganization.id;
        params.organization_id = orgId;
      }

      const response = await axios.get('/api/content-drafts', {
        params,
        withCredentials: true
      });
      setDrafts(response.data.drafts || []);
    } catch (err) {
      console.error('Error fetching content drafts:', err);
      modal.error(err.response?.data?.error || 'Failed to load content drafts');
    } finally {
      setLoading(false);
    }
  };

  // Update one or more drafts directly in local state instead of re-fetching from the
  // server - keeps the table mounted and avoids a full-panel loading flash for what's
  // otherwise an instant change.
  const updateDraftsLocally = (draftIds, fields) => {
    setDrafts(prevDrafts => prevDrafts.map(d =>
      draftIds.includes(d._id) ? { ...d, ...fields } : d
    ));
  };

  const fetchActivityLogs = async () => {
    try {
      setLoadingLogs(true);

      // Build query parameters based on selected brand/org
      const params = { entity_type: 'content_draft', limit: 50 };
      if (selectedBrand && selectedBrand !== 'all') {
        const brandId = selectedBrand._id || selectedBrand.id;
        params.brand_id = brandId;
      }
      if (selectedOrganization && selectedOrganization !== 'all') {
        const orgId = selectedOrganization._id || selectedOrganization.id;
        params.organization_id = orgId;
      }

      const response = await axios.get('/api/activity-logs', {
        params,
        withCredentials: true
      });
      setActivityLogs(response.data.logs || []);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Checkbox: neutral toggle. If item is in any state, clear all. If in no state, add to neutral.
  const toggleRowSelect = (brandId, draftId) => {
    const isNeutral = (selectedDrafts[brandId] || []).includes(draftId);
    const isApproved = (approvedDrafts[brandId] || []).includes(draftId);
    const isDiscarded = (discardedDrafts[brandId] || []).includes(draftId);
    if (isNeutral || isApproved || isDiscarded) {
      setSelectedDrafts(prev => ({ ...prev, [brandId]: (prev[brandId] || []).filter(id => id !== draftId) }));
      setApprovedDrafts(prev => ({ ...prev, [brandId]: (prev[brandId] || []).filter(id => id !== draftId) }));
      setDiscardedDrafts(prev => ({ ...prev, [brandId]: (prev[brandId] || []).filter(id => id !== draftId) }));
    } else {
      setSelectedDrafts(prev => ({ ...prev, [brandId]: [...(prev[brandId] || []), draftId] }));
    }
  };

  // ✓ Approve button: stage for send. Mutually exclusive with neutral and discarded.
  const stageForSend = (brandId, draftId) => {
    const isApproved = (approvedDrafts[brandId] || []).includes(draftId);
    if (isApproved) {
      setApprovedDrafts(prev => ({ ...prev, [brandId]: (prev[brandId] || []).filter(id => id !== draftId) }));
    } else {
      setApprovedDrafts(prev => ({ ...prev, [brandId]: [...(prev[brandId] || []), draftId] }));
      setSelectedDrafts(prev => ({ ...prev, [brandId]: (prev[brandId] || []).filter(id => id !== draftId) }));
      setDiscardedDrafts(prev => ({ ...prev, [brandId]: (prev[brandId] || []).filter(id => id !== draftId) }));
    }
  };

  // ✗ Discard button: stage for discard. Mutually exclusive with neutral and approved.
  const stageForDiscard = (brandId, draftId) => {
    const isDiscarded = (discardedDrafts[brandId] || []).includes(draftId);
    if (isDiscarded) {
      setDiscardedDrafts(prev => ({ ...prev, [brandId]: (prev[brandId] || []).filter(id => id !== draftId) }));
    } else {
      setDiscardedDrafts(prev => ({ ...prev, [brandId]: [...(prev[brandId] || []), draftId] }));
      setSelectedDrafts(prev => ({ ...prev, [brandId]: (prev[brandId] || []).filter(id => id !== draftId) }));
      setApprovedDrafts(prev => ({ ...prev, [brandId]: (prev[brandId] || []).filter(id => id !== draftId) }));
    }
  };

  const selectAllDrafts = (brandId, drafts) => {
    const actionable = drafts.filter(d => canEditOrSend(d.status));
    const actionableIds = actionable.map(d => d._id);
    const neutral = selectedDrafts[brandId] || [];
    const approved = approvedDrafts[brandId] || [];
    const discarded = discardedDrafts[brandId] || [];
    const allSelected = actionableIds.length > 0 && actionableIds.every(id =>
      neutral.includes(id) || approved.includes(id) || discarded.includes(id)
    );
    if (allSelected) {
      setSelectedDrafts(prev => ({ ...prev, [brandId]: [] }));
      setApprovedDrafts(prev => ({ ...prev, [brandId]: [] }));
      setDiscardedDrafts(prev => ({ ...prev, [brandId]: [] }));
    } else {
      setSelectedDrafts(prev => ({ ...prev, [brandId]: actionableIds }));
      setApprovedDrafts(prev => ({ ...prev, [brandId]: [] }));
      setDiscardedDrafts(prev => ({ ...prev, [brandId]: [] }));
    }
  };

  // Green Light brands auto-approve instantly on send, so there's no client to
  // route the item to - skip the recipient picker entirely for those brands.
  const isGreenLightBrand = (brandId) => {
    const draft = drafts.find(d => (d.brand?._id || d.brand?.id || 'unknown') === brandId);
    return !!(draft && draft.brand && draft.brand.green_light_mode === 'green');
  };

  const handleBatchSend = (brandId) => {
    const neutral = selectedDrafts[brandId] || [];
    const approved = approvedDrafts[brandId] || [];
    const ids = [...new Set([...neutral, ...approved])];
    if (ids.length === 0) return;

    if (isGreenLightBrand(brandId)) {
      handleRecipientsSelected([], { brandId, draftIds: ids });
      return;
    }

    // Open recipient selector instead of confirmation
    setRecipientModal({
      open: true,
      brandId: brandId,
      draftIds: ids
    });
  };

  // Posts one send-to-client request for a draft. A requires_confirmation
  // response is not an error, just a pending decision - resolves either way.
  const postSendDraft = (id, recipientIds, overrideFlags) => {
    return axios.post(`/api/content-drafts/${id}/send-to-client`, {
      recipient_ids: recipientIds,
      ...(overrideFlags ? { confirm_override_flags: true } : {})
    }, { withCredentials: true }).then(res => res.data);
  };

  const confirmFlaggedOverride = (flaggedItems) => {
    const list = flaggedItems.map(i => `- ${i.title}`).join('\n');
    return modal.confirm(
      `${flaggedItems.length} item(s) are flagged for review and have not been confirmed as grounded in the source material:\n\n${list}\n\nSend anyway?`,
      'Flagged Content',
      { confirmText: 'Send Anyway', cancelText: 'Cancel' }
    );
  };

  const handleRecipientsSelected = async (recipientIds, context = recipientModal) => {
    const { brandId, draftIds } = context;

    try {
      setSendingBatch(true);

      // Pass 1: attempt every draft; flagged ones come back asking for confirmation
      // instead of being sent.
      const results = await Promise.all(draftIds.map(id => postSendDraft(id, recipientIds, false)));

      const sentIds = [];
      const pendingIds = [];
      const allFlaggedItems = [];
      draftIds.forEach((id, i) => {
        if (results[i].requires_confirmation) {
          pendingIds.push(id);
          allFlaggedItems.push(...results[i].flagged_items);
        } else {
          sentIds.push(id);
        }
      });

      // Pass 2: if anything was deferred, ask once for the whole batch.
      if (pendingIds.length > 0) {
        const proceed = await confirmFlaggedOverride(allFlaggedItems);
        if (proceed) {
          await Promise.all(pendingIds.map(id => postSendDraft(id, recipientIds, true)));
          sentIds.push(...pendingIds);
        }
      }

      if (sentIds.length > 0) {
        const isGreenLight = recipientIds.length === 0;
        const sentMessage = isGreenLight
          ? `Sent ${sentIds.length} draft(s) - instantly approved (Green Light)`
          : `Sent ${sentIds.length} draft(s) to ${recipientIds.length} recipient(s)`;
        modal.toast(sentMessage, 'success');
        updateDraftsLocally(sentIds, isGreenLight ? {
          status: 'approved',
          sent_to_client_at: new Date().toISOString(),
          response_status: 'approved',
          client_decision_at: new Date().toISOString()
        } : {
          status: 'sent_to_client',
          sent_to_client_at: new Date().toISOString(),
          response_status: 'pending'
        });
        // Green Light approved it instantly server-side - refresh the tracker
        // now instead of waiting for the next poll.
        if (isGreenLight) setProgressRefreshKey(k => k + 1);
      }

      // Only clear selection for drafts that actually got sent - leave anything
      // the operator declined to override still staged for them to decide on.
      setSelectedDrafts(prev => ({ ...prev, [brandId]: (prev[brandId] || []).filter(id => !sentIds.includes(id)) }));
      setApprovedDrafts(prev => ({ ...prev, [brandId]: (prev[brandId] || []).filter(id => !sentIds.includes(id)) }));
      setRecipientModal({ open: false, brandId: null, draftIds: [] });
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to send drafts';
      modal.error(errorMessage);

      // If assignment error, refresh the drafts list to remove items user can no longer access
      if (errorMessage.includes('no longer assigned')) {
        setSelectedDrafts(prev => ({ ...prev, [brandId]: [] }));
        setApprovedDrafts(prev => ({ ...prev, [brandId]: [] }));
        fetchDrafts();
      }
    } finally {
      setSendingBatch(false);
    }
  };

  // Discard all checked/staged-for-discard drafts across every brand in a single confirm
  const handleBatchDiscard = async () => {
    const ids = [...new Set([
      ...Object.values(selectedDrafts).flat(),
      ...Object.values(discardedDrafts).flat()
    ])];
    if (ids.length === 0) return;
    const result = await modal.confirm(`Discard ${ids.length} content draft${ids.length !== 1 ? 's' : ''}?`, 'Confirm Discard');
    if (!result) return;
    try {
      await Promise.all(ids.map(id =>
        axios.post(`/api/content-drafts/${id}/discard`, {}, { withCredentials: true })
      ));
      updateDraftsLocally(ids, { status: 'discarded', discarded_at: new Date().toISOString() });
      setSelectedDrafts({});
      setDiscardedDrafts({});
    } catch (err) {
      modal.error(err.response?.data?.error || 'Failed to discard drafts');
    }
  };

  const handleOperatorImageConfirm = async ({ croppedImage, originalImage, cropData, backgroundColor }) => {
    const draftId = operatorImageModal.draft._id;

    try {
      setUploadingDraft(draftId);
      const formData = new FormData();

      // Add cropped image (this is what gets displayed) - preserve PNG format for transparency
      formData.append('media', new File([croppedImage], 'cropped-image.png', { type: 'image/png' }));

      // Add original image if it's a new upload
      if (originalImage) {
        formData.append('original_media', originalImage);
      }

      // Add crop data
      formData.append('crop_data', JSON.stringify(cropData));

      // Background color composited behind transparent images
      formData.append('background_color', backgroundColor || '');

      const response = await axios.post(`/api/content-drafts/${draftId}/media`, formData, {
        withCredentials: true
      });

      // Update the draft in place instead of refetching the whole list
      setDrafts(prevDrafts => prevDrafts.map(d =>
        d._id === draftId
          ? {
              ...d,
              media_url: response.data.media_url,
              media_url_original: response.data.media_url_original || d.media_url_original,
              media_crop_data: cropData,
              background_color: backgroundColor
            }
          : d
      ));

      setOperatorImageModal({ open: false, draft: null });
      modal.success('Image updated successfully');
    } catch (err) {
      modal.error(err.response?.data?.error || 'Failed to upload media');
    } finally {
      setUploadingDraft(null);
    }
  };

  const handleMediaUpload = async (draftId, file) => {
    if (!file) return;

    try {
      setUploadingDraft(draftId);
      const formData = new FormData();
      formData.append('media', file);
      await axios.post(`/api/content-drafts/${draftId}/media`, formData, {
        withCredentials: true
      });
      fetchDrafts();
    } catch (err) {
      modal.error(err.response?.data?.error || 'Failed to upload media');
    } finally {
      setUploadingDraft(null);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      await axios.put(
        `/api/content-drafts/${editModal.draft._id}/content`,
        editForm,
        { withCredentials: true }
      );

      // Update local state instead of refreshing
      const draftId = editModal.draft._id;
      setDrafts(prevDrafts => prevDrafts.map(d => {
        if (d._id !== draftId) return d;
        const updated = { ...d, body: editForm.body, hashtags: editForm.hashtags };
        // Social posts don't have titles - the backend ignores headline edits for them
        if (d.content_type !== 'social') {
          updated.headline = editForm.headline;
        }
        // If editing a revision_requested item, mark it as revised (matches backend behavior)
        if (d.status === 'revision_requested') {
          updated.has_been_revised = true;
        }
        return updated;
      }));

      closeEditModal();
    } catch (err) {
      modal.error(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };


  const handleClearFlag = async () => {
    const confirmed = await modal.confirm(
      'This removes the "Needs Review" flag from this content. Only clear it once you\'ve verified the content is accurate.',
      'Clear Review Flag',
      { confirmText: 'Clear Flag', cancelText: 'Cancel' }
    );
    if (!confirmed) return;

    const draftId = editModal.draft._id;
    try {
      setClearingFlag(true);
      await axios.put(`/api/content-drafts/${draftId}/clear-flag`, {}, { withCredentials: true });

      setDrafts(prevDrafts => prevDrafts.map(d =>
        d._id === draftId ? { ...d, flagged: false, flag_reason: null } : d
      ));
      setEditModal(prev => prev.draft && prev.draft._id === draftId
        ? { ...prev, draft: { ...prev.draft, flagged: false, flag_reason: null } }
        : prev
      );
    } catch (err) {
      modal.error(err.response?.data?.error || 'Failed to clear review flag');
    } finally {
      setClearingFlag(false);
    }
  };

  const openEditModal = (draft) => {
    // Extract hashtags from body if they haven't been migrated yet
    let hashtags = draft.hashtags || [];
    let body = draft.body || '';

    // If hashtags field is empty but body has hashtags, extract them
    if (hashtags.length === 0 && body) {
      const hashtagMatches = body.match(/#\w+/g) || [];
      if (hashtagMatches.length > 0) {
        hashtags = hashtagMatches;
        // Clean body of hashtags
        body = body.replace(/\n\s*(?:#\w+\s*)+\s*$/g, '').replace(/(?:#\w+\s*)+\s*$/g, '').trim();
      }
    }

    const initialForm = {
      headline: draft.headline || draft.title || '',
      body: body,
      hashtags: hashtags
    };
    setEditForm(initialForm);
    setEditFormOriginal(initialForm);
    setSourceViewer({ open: false, loading: false, source: null, copied: false });
    setIdeaViewer({ open: false, loading: false, idea: null });
    setIdeaSourceFallback([]);
    setEditModal({ open: true, draft });

    if ((!draft.sources || draft.sources.length === 0) && draft.idea_id && draft.ideas_doc_id) {
      axios.get(`/api/ideas/${draft.ideas_doc_id}/idea/${draft.idea_id}/sources`, { withCredentials: true })
        .then(response => setIdeaSourceFallback(response.data.sources || []))
        .catch(err => console.error('Error loading idea source fallback:', err));
    }
  };

  const closeEditModal = () => {
    setEditModal({ open: false, draft: null });
    setSourceViewer({ open: false, loading: false, source: null, copied: false });
    setIdeaViewer({ open: false, loading: false, idea: null });
    setIdeaSourceFallback([]);
  };

  const EDIT_FORM_COMPARE_KEYS = ['headline', 'body', 'hashtags'];
  const isEditFormUnchanged = () => {
    if (!editFormOriginal) return true;
    return EDIT_FORM_COMPARE_KEYS.every(
      key => JSON.stringify(editForm[key]) === JSON.stringify(editFormOriginal[key])
    );
  };

  // Open the side-by-side source viewer pane for one of a content draft's
  // attached sources. Library-backed sources only carry name/type/date (no
  // content, to keep the drafts list payload small) - fetch the full source doc
  // on demand. Ad-hoc sources (typed/pasted directly, no backing Source document)
  // already carry their content inline, so no fetch is needed for those.
  const viewDraftSource = async (sourceStub) => {
    if (sourceViewer.open && sourceViewer.source && sourceViewer.source._id === sourceStub._id) {
      closeSourceViewer();
      return;
    }
    if (!sourceStub._id) {
      setSourceViewer({ open: true, loading: false, source: sourceStub, copied: false });
      return;
    }
    setSourceViewer({ open: true, loading: true, source: null, copied: false });
    try {
      const response = await axios.get('/api/sources', {
        params: { brand_id: editModal.draft?.brand_id },
        withCredentials: true
      });
      const fullSource = (response.data.sources || []).find(s => s._id === sourceStub._id);
      setSourceViewer({ open: true, loading: false, source: fullSource || sourceStub, copied: false });
    } catch (err) {
      console.error('Error loading source content:', err);
      setSourceViewer({ open: true, loading: false, source: sourceStub, copied: false });
    }
  };

  const closeSourceViewer = () => {
    setSourceViewer({ open: false, loading: false, source: null, copied: false });
  };

  // Open/close the read-only "original idea" pane - toggled the same way a
  // source is, never shown automatically when the Edit Content Draft modal opens.
  const viewOriginalIdea = async (draft) => {
    if (ideaViewer.open) {
      closeIdeaViewer();
      return;
    }
    setIdeaViewer({ open: true, loading: true, idea: null });
    try {
      const response = await axios.get(`/api/ideas/${draft.ideas_doc_id}/idea/${draft.idea_id}`, {
        withCredentials: true
      });
      setIdeaViewer({ open: true, loading: false, idea: response.data.idea || null });
    } catch (err) {
      console.error('Error loading original idea:', err);
      setIdeaViewer({ open: true, loading: false, idea: null });
    }
  };

  const closeIdeaViewer = () => {
    setIdeaViewer({ open: false, loading: false, idea: null });
  };

  const copyDraftSourceContent = async () => {
    const source = sourceViewer.source;
    if (!source) return;

    let contentToCopy = '';
    if (source.source_type === 'url') {
      contentToCopy = source.original_url || source.content || '';
    } else if (typeof source.content === 'object' && source.content !== null) {
      contentToCopy = source.content.formatted || source.content.simple || JSON.stringify(source.content, null, 2);
    } else {
      contentToCopy = source.content || '';
    }

    if (contentToCopy) {
      try {
        await navigator.clipboard.writeText(contentToCopy);
        setSourceViewer(prev => ({ ...prev, copied: true }));
        setTimeout(() => setSourceViewer(prev => ({ ...prev, copied: false })), 2000);
      } catch (err) {
        console.error('Error copying to clipboard:', err);
      }
    }
  };

  const getDraftSourceIcon = (type) => {
    const iconMap = {
      url: 'link',
      text: 'doc',
      image: 'picture',
      video: 'play',
      audio: 'play',
      transcription: 'comment-empty',
      squadcast_transcript: 'comment-empty',
      meeting: 'comment-empty',
      pdf: 'doc-text',
      google_drive: 'google',
      brand_profile: 'user'
    };
    return iconMap[type] || 'doc';
  };

  const getDraftSourceTypeName = (source) => {
    const type = source.source_type;
    const metadata = source.metadata || {};
    if ((type === 'transcription' || type === 'meeting') && (metadata.is_squadcast || metadata.squadcast_session_id)) {
      return 'SQUADCAST TRANSCRIPT';
    }
    const typeMap = {
      meeting: 'TRANSCRIPT',
      transcription: 'TRANSCRIPT',
      squadcast_transcript: 'SQUADCAST TRANSCRIPT',
      brand_profile: 'BRAND PROFILE',
      url: 'URL',
      text: 'TEXT',
      image: 'IMAGE',
      video: 'VIDEO',
      audio: 'AUDIO',
      pdf: 'PDF',
      google_drive: 'GOOGLE DRIVE'
    };
    return typeMap[type] || (type ? type.toUpperCase() : 'SOURCE');
  };

  // Renders a source's content inside the side-by-side viewer pane
  const renderDraftSourceContent = (source) => {
    if (source.source_type === 'url') {
      return (
        <div className="rc-source-pane-section">
          <div className="rc-source-pane-url-display">
            <Icon name="link" size="1.1rem" />
            <a
              href={source.original_url || source.content}
              target="_blank"
              rel="noopener noreferrer"
              className="rc-source-pane-url-value"
            >
              {source.original_url || source.content}
            </a>
          </div>
          {source.content && source.content !== source.original_url && (
            <div>
              <h5 className="rc-source-pane-subheading">Scraped Content</h5>
              <pre className="rc-source-pane-content-text">{source.content}</pre>
            </div>
          )}
        </div>
      );
    }

    if (source.source_type === 'meeting' || source.source_type === 'transcription' || source.source_type === 'squadcast_transcript') {
      const content = source.content;
      if (!content) {
        return <p className="rc-source-pane-empty">No transcript available.</p>;
      }
      const textContent = typeof content === 'object' && content !== null
        ? (content.formatted || content.simple || JSON.stringify(content, null, 2))
        : content;
      return <pre className="rc-source-pane-content-text rc-source-pane-transcript-text">{textContent}</pre>;
    }

    if (source.source_type === 'brand_profile') {
      const content = source.content;
      if (!content) {
        return <p className="rc-source-pane-empty">No brand profile data available.</p>;
      }
      let profileData;
      try {
        profileData = typeof content === 'string' ? JSON.parse(content) : content;
      } catch (e) {
        return <pre className="rc-source-pane-content-text">{content}</pre>;
      }

      const renderSection = (title, value, isList = false) => {
        if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && !value.trim())) {
          return null;
        }
        return (
          <div className="rc-source-pane-profile-section" key={title}>
            <h5 className="rc-source-pane-subheading">{title}</h5>
            {isList ? (
              <ul className="rc-source-pane-profile-list">
                {value.map((item, idx) => <li key={idx}>{item}</li>)}
              </ul>
            ) : (
              <p className="rc-source-pane-profile-text">{value}</p>
            )}
          </div>
        );
      };

      return (
        <div className="rc-source-pane-section">
          {renderSection('Target Audience', profileData.audience)}
          {renderSection('Mission & Core Values', profileData.mission_core_values)}
          {renderSection('Value Propositions', profileData.value_propositions, true)}
          {renderSection('Key Differentiators', profileData.differentiators, true)}
          {renderSection('Products & Services', profileData.products_services)}
          {renderSection('Locations', profileData.locations, true)}
          {renderSection('Ideal Clients', profileData.ideal_clients)}
          {renderSection('Industry Details', profileData.industry_details)}
          {renderSection('Competitors', profileData.competitors, true)}
          {renderSection('Offers', profileData.offers, true)}
        </div>
      );
    }

    return source.content
      ? <pre className="rc-source-pane-content-text">{source.content}</pre>
      : <p className="rc-source-pane-empty">No content available.</p>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatCommentTimestamp = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${month}/${day} ${hours}:${minutesStr} ${ampm}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_operator_review': { label: 'Needs Review', color: '#F59E0B', icon: null },
      'approved_operator':       { label: 'Operator Approved', color: '#10B981', icon: null },
      'sent_to_client':       { label: 'Sent to Client', color: '#3B82F6', icon: null },
      'approved':             { label: 'Client Approved', color: '#059669', icon: null },
      'denied':               { label: 'Client Denied', color: 'var(--color-error)', icon: null },
      'revision_requested':   { label: 'Revision Requested', color: '#8B5CF6', icon: null },
      'discarded':            { label: 'Discarded', color: '#6B7280', icon: null },
      'scheduled':            { label: 'Client Approved', color: '#059669', icon: null }
    };
    const config = statusConfig[status] || { label: status, color: '#6B7280', icon: null };
    return (
      <span className="rc-status-badge" style={{ backgroundColor: config.color }}>
        {config.icon && <Icon name={config.icon} size="0.875rem" />}
        {config.label}
      </span>
    );
  };

  const getContentTypeBadge = (type) => {
    const typeConfig = {
      'social':     { label: 'Social',      color: '#3B82F6' },
      'blog':       { label: 'Blog',         color: '#10B981' },
      'video':      { label: 'Video',        color: '#EF4444' },
      'podcast':    { label: 'Podcast',      color: '#8B5CF6' },
      'newsletter': { label: 'Newsletter',   color: '#F59E0B' }
    };
    const config = typeConfig[type] || { label: type || '-', color: '#6B7280' };
    return (
      <span className="rc-type-badge" style={{ backgroundColor: config.color }}>
        {config.label}
      </span>
    );
  };

  const getPreviewImageStyle = (draft) => {
    if (!draft.media_url || !draft.media_crop_data || !previewImageDimensions) {
      return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '100%',
        maxHeight: '100%',
      };
    }

    const cropData = draft.media_crop_data;

    const containerAspect = 4 / 3;
    const imageAspect = previewImageDimensions.width / previewImageDimensions.height;

    const editorWidth = 450;
    const editorHeight = editorWidth / containerAspect;

    const cropPercentX = (cropData.cropPosition.x / editorWidth) * 100;
    const cropPercentY = (cropData.cropPosition.y / editorHeight) * 100;

    return {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: `translate(calc(-50% + ${cropPercentX}%), calc(-50% + ${cropPercentY}%)) scale(${cropData.zoom})`,
      transformOrigin: 'center',
      width: imageAspect > containerAspect ? 'auto' : '100%',
      height: imageAspect > containerAspect ? '100%' : 'auto',
      maxWidth: 'none',
      maxHeight: 'none',
    };
  };

  const renderContentPreviewModal = () => {
    if (!viewingDraft) return null;
    const draft = viewingDraft;
    const brandName = draft.brand?.name || 'Brand';
    const brandLogo = draft.brand?.icon_url;
    const isSocial = draft.content_type === 'social';
    const isBlog = draft.content_type === 'blog';
    const dateStr = formatDate(draft.client_decision_at || draft.created_at);

    const extractHashtags = (text) => (text || '').match(/#\w+/g) || [];
    const hashtags = [...new Set([
      ...extractHashtags(draft.production_notes || ''),
      ...extractHashtags(draft.body || '')
    ])];
    const cleanBody = (draft.body || '').replace(/#\w+/g, '').trim();

    const renderMedia = () => draft.media_url && (
      <div
        className="mock-media-wrapper"
        style={draft.background_color && draft.background_color !== 'transparent' ? { backgroundColor: draft.background_color } : undefined}
      >
        {draft.media_crop_data && getUsableOriginalUrl(draft) ? (
          <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
            <img
              src={getUsableOriginalUrl(draft)}
              alt="Preview"
              style={getPreviewImageStyle(draft)}
            />
          </div>
        ) : (
          <img src={draft.media_url} alt="Content media" className="mock-post-image" />
        )}
      </div>
    );

    return (
      <div className="mock-modal-overlay" onClick={() => setViewingDraft(null)}>
        <div className="mock-modal" onClick={(e) => e.stopPropagation()}>
          <div className="mock-modal-header">
            <h3>Content Preview</h3>
            <button className="modal-close-btn" onClick={() => setViewingDraft(null)} aria-label="Close" />
          </div>

          <div className="mock-modal-body">
            {/* Post Preview - using same structure as OperatorImageModal */}
            <div className="op-image-preview-mock">
              {isSocial ? (
                <div className="mock-social-post">
                  <div className="mock-post-header">
                    <div className="mock-author-info">
                      {brandLogo ? (
                        <BrandIcon src={brandLogo} alt={brandName} className="mock-brand-avatar" />
                      ) : (
                        <div className="mock-brand-initial">{brandName ? brandName.charAt(0).toUpperCase() : 'B'}</div>
                      )}
                      <div>
                        <div className="mock-brand-name">{brandName || 'Brand Name'}</div>
                        <div className="mock-post-meta">{dateStr} · <Icon name="link" size="0.9rem" /></div>
                      </div>
                    </div>
                    <span className="mock-type-tag mock-type-social">SOCIAL</span>
                  </div>
                  <div className="mock-post-body">
                    {formatBlogBody(cleanBody || draft.body || 'Content preview').map(block => (
                      block.type === 'heading'
                        ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                        : <p key={block.key} className="mock-post-paragraph">{block.text}</p>
                    ))}
                  </div>
                  {hashtags.length > 0 && (
                    <div className="mock-hashtags">
                      {hashtags.map((h, i) => <span key={i} className="mock-hashtag">{h} </span>)}
                    </div>
                  )}
                  {renderMedia()}
                  <div className="mock-social-actions">
                    <span className="mock-social-btn" style={{ color: 'rgb(225, 48, 108)' }}><Icon name="heart" size="0.9rem" /> Like</span>
                    <span className="mock-social-btn"><Icon name="comment-empty" size="0.9rem" /> Comment</span>
                    <span className="mock-social-btn"><Icon name="share" size="0.9rem" /> Share</span>
                    <span className="mock-social-btn"><Icon name="paper-plane-empty" size="0.9rem" /> Send</span>
                  </div>
                </div>
              ) : isBlog ? (
                <div className="mock-blog-post">
                  <div className="mock-blog-url-bar">
                    <span className="mock-blog-url">
                      {draft.call_to_action || `https://blog.example.com/${(draft.headline || draft.title || 'post').toLowerCase().replace(/\s+/g, '-')}`}
                    </span>
                    <span className="mock-type-tag mock-type-blog">BLOG</span>
                  </div>
                  {draft.media_url && (
                    <div className="mock-blog-image-wrapper">
                      <img src={draft.media_url} alt="Blog header" className="mock-blog-image" />
                    </div>
                  )}
                  {draft.headline && <h2 className="mock-blog-title">{draft.headline}</h2>}
                  <div className="mock-blog-date">{dateStr}</div>
                  <div className="mock-blog-body">
                    {formatBlogBody(draft.body).map(block => (
                      block.type === 'heading'
                        ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                        : <p key={block.key} className="mock-blog-paragraph">{block.text}</p>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mock-generic-post">
                  <div className="mock-post-header">
                    <div className="mock-author-info">
                      {brandLogo ? (
                        <BrandIcon src={brandLogo} alt={brandName} className="mock-brand-avatar" />
                      ) : (
                        <div className="mock-brand-initial">{brandName ? brandName.charAt(0).toUpperCase() : 'B'}</div>
                      )}
                      <div>
                        <div className="mock-brand-name">{brandName || 'Brand Name'}</div>
                        <div className="mock-post-meta">{dateStr}</div>
                      </div>
                    </div>
                    {getContentTypeBadge(draft.content_type)}
                  </div>
                  {draft.headline && <div className="mock-post-title">{draft.headline}</div>}
                  <div className="mock-post-body">
                    {formatBlogBody(draft.body).map(block => (
                      block.type === 'heading'
                        ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                        : <p key={block.key} className="mock-post-paragraph">{block.text}</p>
                    ))}
                  </div>
                  {renderMedia()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const filterDrafts = (all) => {
    const workflowDrafts = all.filter(d =>
      d.status !== 'sent_to_client' && d.status !== 'approved' && d.status !== 'scheduled'
    );
    if (filterStatus === 'all') return workflowDrafts;
    return workflowDrafts.filter(d => d.status === filterStatus);
  };

  const historyDrafts = drafts.filter(d => {
    // For operators: sent_to_client is an action they took
    // For clients: sent_to_client means pending (waiting for their action)
    if (user?.is_operator) {
      return ['sent_to_client', 'approved', 'denied', 'revision_requested', 'scheduled', 'discarded'].includes(d.status);
    } else {
      // Clients only see items where THEY took action
      return ['approved', 'denied', 'revision_requested', 'scheduled', 'discarded'].includes(d.status);
    }
  });

  const canEditOrSend = (status) =>
    status === 'pending_operator_review' || status === 'revision_requested' || status === 'denied';

  // No need for client-side org/brand filtering - server handles it now
  // Keeping this function for backwards compatibility but it just returns the list as-is
  const filterByOrgBrand = (draftList) => {
    return draftList;
  };

  // Group drafts by brand
  const groupDraftsByBrand = (draftList) => {
    const groups = {};
    draftList.forEach(draft => {
      const brandId = draft.brand?._id || draft.brand?.id || 'unknown';
      if (!groups[brandId]) {
        groups[brandId] = {
          brandId,
          brandName: draft.brand?.name || 'Unknown Brand',
          brandLogo: draft.brand?.icon_url,
          orgName: draft.organization?.name || '',
          drafts: [],
          latestDate: null
        };
      }
      groups[brandId].drafts.push(draft);
      const date = draft.created_at;
      if (!groups[brandId].latestDate || date > groups[brandId].latestDate) {
        groups[brandId].latestDate = date;
      }
    });
    return Object.values(groups);
  };

  if (loading && drafts.length === 0) {
    return (
      <div className="rc-container">
        <div className="rc-content-wrapper">
          <div className="rc-loading">
            <div className="rc-spinner"></div>
            <p>Loading content drafts...</p>
          </div>
        </div>
      </div>
    );
  }

  const currentBrandGroups = groupDraftsByBrand(filterByOrgBrand(filterDrafts(drafts)));
  const historyBrandGroups = groupDraftsByBrand(filterByOrgBrand(historyDrafts));

  return (
    <div className="rc-container">
      <div className="rc-content-wrapper">
        {/* Tabs */}
        <div className="rc-tabs-container">
          <button
            className={`rc-tab ${activeTab === 'current' ? 'active' : ''}`}
            onClick={() => setActiveTab('current')}
          >
            Review
          </button>
          <button
            className={`rc-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>

        <MonthlyProgressTracker brandId={brandId} refreshKey={progressRefreshKey} />

        {activeTab === 'history' ? (
          /* ── HISTORY TAB ── */
          <div className="rc-section">
            <div className="rc-header-controls">
              {historyBrandGroups.length > 0 && (() => {
                const totalHistoryCount = historyBrandGroups.reduce((sum, g) => sum + g.drafts.length, 0);
                return (
                  <div className="rc-pagination-info">
                    Showing {Math.min((historyPage - 1) * HISTORY_ITEMS_PER_PAGE + 1, totalHistoryCount)}-{Math.min(historyPage * HISTORY_ITEMS_PER_PAGE, totalHistoryCount)} of {totalHistoryCount} items
                  </div>
                );
              })()}
              <div className="rc-header-actions">
                <button className="rc-btn-refresh" onClick={fetchDrafts}>Refresh</button>
              </div>
            </div>

            {historyBrandGroups.length === 0 ? (
              <div className="rc-empty-state">
                <div className="rc-empty-icon"><Icon name="content" size="3rem" /></div>
                <h3>No History Yet</h3>
                <p>Approved, denied, and archived content will show up here once there's activity.</p>
              </div>
            ) : (
              (() => {
                // Flatten all history drafts for pagination
                const allHistoryDraftsRaw = historyBrandGroups.flatMap(group =>
                  group.drafts.map(draft => ({ ...draft, groupBrandName: group.brandName, groupBrandLogo: group.brandLogo }))
                );
                const allHistoryDrafts = sortData(allHistoryDraftsRaw, historyDraftsSortConfig);
                const requestHistorySort = (key) => setHistoryDraftsSortConfig(prev =>
                  prev?.key === key && prev.direction === 'asc' ? { key, direction: 'desc' } : { key, direction: 'asc' }
                );
                const getHistorySortClass = (key) => historyDraftsSortConfig?.key === key ? `sortable-th sort-${historyDraftsSortConfig.direction}` : 'sortable-th';

                // Calculate pagination
                const totalPages = Math.ceil(allHistoryDrafts.length / HISTORY_ITEMS_PER_PAGE);
                const startIndex = (historyPage - 1) * HISTORY_ITEMS_PER_PAGE;
                const endIndex = startIndex + HISTORY_ITEMS_PER_PAGE;
                const paginatedDrafts = allHistoryDrafts.slice(startIndex, endIndex);

                return (
                  <div className="rc-list">
                    <div className="rc-brand-card">
                      <div className="rc-brand-content">
                        <div className="rc-table-wrapper">
                          <table className="rc-drafts-table">
                            <thead>
                              <tr>
                                <th style={{ width: '80px' }} className={getHistorySortClass('groupBrandName')} onClick={() => requestHistorySort('groupBrandName')}>Brand</th>
                                <th className={getHistorySortClass('title')} onClick={() => requestHistorySort('title')}>Content</th>
                                <th style={{ width: '90px' }}>Upload</th>
                                <th style={{ width: '120px' }} className={getHistorySortClass('updated_at')} onClick={() => requestHistorySort('updated_at')}>Action Date</th>
                                <th style={{ width: '80px' }}>Comments</th>
                                <th style={{ width: '120px' }} className={getHistorySortClass('content_type')} onClick={() => requestHistorySort('content_type')}>Type</th>
                                <th style={{ width: '180px' }} className={getHistorySortClass('status')} onClick={() => requestHistorySort('status')}>Current Status</th>
                                <th style={{ width: '80px' }}>View</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedDrafts.map((draft) => (
                              <tr key={draft._id} className="rc-draft-row">
                                <td className="rc-brand-cell">
                                  {draft.brand?.icon_url ? (
                                    <BrandIcon src={draft.brand.icon_url} alt={draft.brand?.name} className="rc-brand-logo-small" />
                                  ) : (
                                    <div className="rc-brand-placeholder-small">
                                      <span>{(draft.brand?.name || 'B').charAt(0).toUpperCase()}</span>
                                    </div>
                                  )}
                                </td>
                                <td className="rc-content-cell">
                                  <div className="rc-content-title-row">
                                    <div className="rc-draft-title">{draft.title || draft.headline}</div>
                                    {draft.flagged && (
                                      <span className="rc-flagged-inline-badge">
                                        <Icon name="flag" size="0.85rem" /> Needs Review
                                      </span>
                                    )}
                                    {draft.status === 'revision_requested' && (
                                      <span className="rc-revision-inline-badge">Revision Requested</span>
                                    )}
                                  </div>
                                  <div className="rc-body-preview">{draft.body || '-'}</div>
                                </td>
                                <td className="rc-upload-cell">
                                  {draft.media_url && (
                                    <div className="rc-media-indicator" title="Has media">
                                      <Icon name="picture" size="1rem" />
                                    </div>
                                  )}
                                </td>
                                <td className="rc-date-cell">
                                  {formatDate(draft.responded_at || draft.client_decision_at || draft.updated_at || draft.created_at)}
                                </td>
                                <td className="rc-comment-cell">
                                  <button
                                    className="rc-comment-icon-btn"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setCommentModalState({
                                        open: true,
                                        draftId: draft._id,
                                        comments: draft.comments || []
                                      });
                                    }}
                                    title="Add comment"
                                    type="button"
                                  >
                                    <Icon name="comment-empty" size="1.5rem" />
                                    {draft.comments && draft.comments.length > 0 && (
                                      <span className="rc-comment-count-badge">{draft.comments.length}</span>
                                    )}
                                  </button>
                                </td>
                                <td>{getContentTypeBadge(draft.content_type)}</td>
                                <td className="rc-status-cell">{getStatusBadge(draft.status)}</td>
                                <td className="rc-view-cell">
                                  <button
                                    className="rc-view-btn"
                                    onClick={() => setViewingDraft(draft)}
                                    title="View content preview"
                                  >
                                    <Icon name="go-to-link" size="1.75rem" />
                                  </button>
                                </td>
                              </tr>
                              ))
                            }
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="rc-pagination-controls">
                          <button
                            className="rc-pagination-btn"
                            onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                            disabled={historyPage === 1}
                          >
                            <Icon name="arrow-left" size="1rem" />
                            Previous
                          </button>
                          <div className="rc-pagination-numbers">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (historyPage <= 3) {
                                pageNum = i + 1;
                              } else if (historyPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = historyPage - 2 + i;
                              }
                              return (
                                <button
                                  key={pageNum}
                                  className={`rc-pagination-number ${historyPage === pageNum ? 'active' : ''}`}
                                  onClick={() => setHistoryPage(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}
                          </div>
                          <button
                            className="rc-pagination-btn"
                            onClick={() => setHistoryPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={historyPage === totalPages}
                          >
                            Next
                            <Icon name="arrow-right" size="1rem" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                );
              })()
            )}
          </div>
        ) : drafts.length === 0 ? (
          /* ── EMPTY STATE ── */
          <>
            <div className="rc-header-controls">
              <div className="rc-header-actions">
                <button className="rc-btn-refresh" onClick={fetchDrafts}>Refresh</button>
              </div>
            </div>
            <div className="rc-empty-state">
              <div className="rc-empty-icon"><Icon name="content" size="3rem" /></div>
              <h3>No Content Drafts Yet</h3>
              <p>Content drafts appear here when a client approves an idea.</p>
            </div>
          </>
        ) : (() => {
            /* ── CURRENT TAB ── */
            // Flatten all drafts into a single list
            const allDraftsRaw = filterByOrgBrand(filterDrafts(drafts));
            const allDrafts = sortData(allDraftsRaw.map(d => ({
              ...d,
              _sortBrand: d.brand?.name || '',
              actionDate: d.responded_at || d.client_decision_at || d.updated_at || d.created_at
            })), opDraftsSortConfig);
            const requestOpSort = (key) => setOpDraftsSortConfig(prev =>
              prev?.key === key && prev.direction === 'asc' ? { key, direction: 'desc' } : { key, direction: 'asc' }
            );
            const getOpSortClass = (key) => opDraftsSortConfig?.key === key ? `sortable-th sort-${opDraftsSortConfig.direction}` : 'sortable-th';

            // Calculate total send/discard counts across all brands
            const allNeutral = Object.values(selectedDrafts).flat();
            const allApproved = Object.values(approvedDrafts).flat();
            const allDiscarded = Object.values(discardedDrafts).flat();
            const totalSendCount = new Set([...allNeutral, ...allApproved]).size;
            const totalDiscardCount = new Set([...allNeutral, ...allDiscarded]).size;

            // Check if all actionable drafts are selected
            const actionableDrafts = allDrafts.filter(d => canEditOrSend(d.status));
            const allSelected = actionableDrafts.length > 0 && actionableDrafts.every(d =>
              allNeutral.includes(d._id) || allApproved.includes(d._id) || allDiscarded.includes(d._id)
            );

            return (
              <>
                <div className="rc-header-controls">
                  <div className="rc-select-batch-group">
                    <label className="rc-select-all-checkbox">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => {
                          if (allSelected) {
                            // Deselect all
                            setSelectedDrafts({});
                            setApprovedDrafts({});
                            setDiscardedDrafts({});
                          } else {
                            // Select all actionable drafts, grouped by brandId
                            const newSelected = {};
                            actionableDrafts.forEach(d => {
                              const brandId = d.brand?._id || d.brand?.id || 'unknown';
                              if (!newSelected[brandId]) newSelected[brandId] = [];
                              newSelected[brandId].push(d._id);
                            });
                            setSelectedDrafts(newSelected);
                            setApprovedDrafts({});
                            setDiscardedDrafts({});
                          }
                        }}
                      />
                      Select All
                    </label>
                    <div className="rc-batch-action-buttons">
                      <button
                        className={`rc-btn-send-batch${totalSendCount === 0 ? ' rc-btn-batch-inactive' : ''}`}
                        onClick={() => {
                          // Send to client for all selected drafts across all brands
                          Object.keys({ ...selectedDrafts, ...approvedDrafts }).forEach(brandId => {
                            handleBatchSend(brandId);
                          });
                        }}
                        disabled={sendingBatch || totalSendCount === 0}
                      >
                        {sendingBatch ? 'Sending...' : `Send${totalSendCount > 0 ? ` ${totalSendCount}` : ''} to Client`}
                      </button>
                      <button
                        className={`rc-btn-discard-batch${totalDiscardCount === 0 ? ' rc-btn-batch-inactive' : ''}`}
                        onClick={handleBatchDiscard}
                        disabled={totalDiscardCount === 0}
                      >
                        Discard{totalDiscardCount > 0 ? ` ${totalDiscardCount}` : ''}
                      </button>
                    </div>
                  </div>
                  <div className="rc-header-actions">
                      <CustomSelect
                        className="rc-filter-select"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="all">All Statuses</option>
                        <option value="pending_operator_review">Needs Review</option>
                        <option value="revision_requested">Revision Requested</option>
                        <option value="denied">Client Denied</option>
                      </CustomSelect>
                    <button className="rc-btn-refresh" onClick={fetchDrafts}>Refresh</button>
                  </div>
                </div>

                {allDrafts.length === 0 ? (
                  <div className="rc-empty">
                    <Icon name="check" size="2rem" />
                    <p>No drafts matching this filter</p>
                  </div>
                ) : (
                  <div className="rc-list">
                    <div className="rc-brand-card">
                      <div className="rc-brand-content">
                      <div className="rc-table-wrapper">
                        <table className="rc-drafts-table">
                          <thead>
                            <tr>
                              <th style={{ width: '40px' }}></th>
                              <th style={{ width: '80px' }} className={getOpSortClass('_sortBrand')} onClick={() => requestOpSort('_sortBrand')}>Brand</th>
                              <th className={getOpSortClass('title')} onClick={() => requestOpSort('title')}>Content</th>
                              <th style={{ width: '90px' }}>Upload</th>
                              <th style={{ width: '120px' }} className={getOpSortClass('actionDate')} onClick={() => requestOpSort('actionDate')}>Action Date</th>
                              <th style={{ width: '80px' }}>Comments</th>
                              <th style={{ width: '120px' }} className={getOpSortClass('content_type')} onClick={() => requestOpSort('content_type')}>Type</th>
                              <th style={{ width: '130px' }}>Actions</th>
                              <th style={{ width: '80px' }}>View</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allDrafts.map((draft) => {
                              const brandId = draft.brand?._id || draft.brand?.id || 'unknown';
                              const canAct = canEditOrSend(draft.status);
                              const isNeutral = (selectedDrafts[brandId] || []).includes(draft._id);
                              const isApproved = (approvedDrafts[brandId] || []).includes(draft._id);
                              const isDiscarded = (discardedDrafts[brandId] || []).includes(draft._id);
                              const isAnyStaged = isNeutral || isApproved || isDiscarded;
                              return (
                                <tr
                                  key={draft._id}
                                  className={`rc-draft-row${draft.status === 'revision_requested' && !draft.has_been_revised ? (draft.source_type === 'operator_created' ? ' rc-operator-created-revision' : ' rc-revision-row') : ''}${draft.status === 'revision_requested' && draft.has_been_revised ? ' rc-revised-row' : ''}${draft.status === 'denied' ? ' rc-denied-row' : ''}${isNeutral ? ' rc-neutral-selected' : ''}${isApproved ? ' rc-selected' : ''}${isDiscarded ? ' rc-discard-staged' : ''}`}
                                >
                                  <td className="rc-checkbox-cell">
                                    {canAct && (
                                      <input
                                        type="checkbox"
                                        checked={isAnyStaged}
                                        onChange={() => toggleRowSelect(brandId, draft._id)}
                                        className="rc-draft-checkbox"
                                      />
                                    )}
                                  </td>
                                  <td className="rc-brand-cell">
                                    {draft.brand?.icon_url ? (
                                      <BrandIcon src={draft.brand.icon_url} alt={draft.brand?.name} className="rc-brand-logo-small" />
                                    ) : (
                                      <div className="rc-brand-placeholder-small">
                                        <span>{(draft.brand?.name || 'B').charAt(0).toUpperCase()}</span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="rc-content-cell">
                                    <div className="rc-content-title-row">
                                      <div className="rc-draft-title">{draft.title || draft.headline}</div>
                                      {draft.flagged && (
                                        <span className="rc-flagged-inline-badge">
                                          <Icon name="flag" size="0.85rem" /> Needs Review
                                        </span>
                                      )}
                                      {draft.status === 'revision_requested' && !draft.has_been_revised && (
                                        <span className="rc-revision-inline-badge">Revision Requested</span>
                                      )}
                                      {draft.status === 'revision_requested' && draft.has_been_revised && (
                                        <span className="rc-revised-inline-badge">Revised</span>
                                      )}
                                    </div>
                                    <div className="rc-body-preview">{draft.body || '-'}</div>
                                  </td>
                                  <td className="rc-upload-cell">
                                    <button
                                      className={`rc-upload-btn${draft.media_url ? ' has-media' : ''}`}
                                      title={draft.media_url ? 'Replace media' : 'Upload media'}
                                      onClick={() => setOperatorImageModal({ open: true, draft })}
                                      disabled={uploadingDraft === draft._id}
                                    >
                                      <Icon
                                        name={uploadingDraft === draft._id ? 'clock' : 'picture'}
                                        size="1rem"
                                      />
                                      {draft.media_url && (
                                        <span className="rc-upload-check">
                                          <Icon name="ok" size="0.7rem" />
                                        </span>
                                      )}
                                    </button>
                                  </td>
                                  <td className="rc-date-cell">
                                    {formatDate(draft.actionDate)}
                                  </td>
                                  <td className="rc-comment-cell">
                                    <button
                                      className="rc-comment-icon-btn"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setCommentModalState({
                                          open: true,
                                          draftId: draft._id,
                                          comments: draft.comments || []
                                        });
                                      }}
                                      title="Add comment"
                                      type="button"
                                    >
                                      <Icon name="comment-empty" size="1.5rem" />
                                      {draft.comments && draft.comments.length > 0 && (
                                        <span className="rc-comment-count-badge">{draft.comments.length}</span>
                                      )}
                                    </button>
                                  </td>
                                  <td>{getContentTypeBadge(draft.content_type)}</td>
                                  <td className="rc-actions-cell">
                                    {canAct && (
                                      <div className="rc-row-actions">
                                        <button
                                          className={`rc-row-btn rc-row-btn-select${isApproved ? ' batched' : ''}`}
                                          title={isApproved ? 'Remove from send batch' : 'Add to send batch'}
                                          onClick={(e) => { e.stopPropagation(); stageForSend(brandId, draft._id); }}
                                        >
                                          <Icon name="ok" size="1rem" />
                                        </button>
                                        <button
                                          className="rc-row-btn rc-row-btn-edit"
                                          title="Edit"
                                          onClick={(e) => { e.stopPropagation(); openEditModal(draft); }}
                                        >
                                          <Icon name="pencil" size="1rem" />
                                        </button>
                                        <button
                                          className={`rc-row-btn rc-row-btn-discard${isDiscarded ? ' batched-discard' : ''}`}
                                          title={isDiscarded ? 'Remove from discard batch' : 'Add to discard batch'}
                                          onClick={(e) => { e.stopPropagation(); stageForDiscard(brandId, draft._id); }}
                                        >
                                          <Icon name="cancel" size="1rem" />
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                  <td className="rc-view-cell">
                                    <button
                                      className="rc-view-btn"
                                      onClick={() => setViewingDraft(draft)}
                                      title="View content preview"
                                    >
                                      <Icon name="go-to-link" size="1.75rem" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
      </div>

      {/* Edit Modal */}
      {editModal.open && (
        <div className="rc-modal-overlay">
          <div className={`rc-modal rc-modal-large ${ideaViewer.open ? 'rc-with-idea-viewer' : ''} ${sourceViewer.open ? 'rc-with-source-viewer' : ''}`}>
          {ideaViewer.open && (
            <div className="rc-modal-idea-col">
              <div className="rc-source-pane-header">
                <div className="rc-source-pane-header-info">
                  <h4>Approved Idea</h4>
                  {ideaViewer.idea && (
                    <div className="rc-source-pane-meta">
                      <span className="rc-source-pane-type-badge">{ideaViewer.idea.content_type}</span>
                    </div>
                  )}
                </div>
                <div className="rc-source-pane-header-actions">
                  <button className="modal-close-btn" onClick={closeIdeaViewer} aria-label="Close approved idea" />
                </div>
              </div>
              <div className="rc-source-pane-body">
                {ideaViewer.loading ? (
                  <p className="rc-source-pane-empty">Loading idea…</p>
                ) : !ideaViewer.idea ? (
                  <p className="rc-source-pane-empty">Could not load the approved idea.</p>
                ) : (
                  <div className="rc-source-pane-section">
                    <div>
                      <h5 className="rc-source-pane-subheading">Title</h5>
                      <p className="rc-source-pane-profile-text">{ideaViewer.idea.title}</p>
                    </div>
                    <div>
                      <h5 className="rc-source-pane-subheading">Synopsis</h5>
                      <p className="rc-source-pane-profile-text">{ideaViewer.idea.synopsis || 'No synopsis provided.'}</p>
                    </div>
                    <div>
                      <h5 className="rc-source-pane-subheading">Key Points</h5>
                      {ideaViewer.idea.key_points && ideaViewer.idea.key_points.length > 0 ? (
                        <ul className="rc-source-pane-profile-list">
                          {ideaViewer.idea.key_points.map((point, idx) => <li key={idx}>{point}</li>)}
                        </ul>
                      ) : (
                        <p className="rc-source-pane-profile-text">No key points provided.</p>
                      )}
                    </div>
                    {ideaViewer.idea.flag_reason && (
                      <div>
                        <h5 className="rc-source-pane-subheading">Flag Reason (at idea stage)</h5>
                        <p className="rc-source-pane-profile-text">{ideaViewer.idea.flag_reason}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="rc-modal-form-col">
            <div className="rc-modal-header">
              <h3>Edit Content Draft</h3>
              <button className="modal-close-btn" onClick={closeEditModal} aria-label="Close" />
            </div>
            <div className="rc-modal-body">
              {editModal.draft?.flagged && (
                <div className="rc-flag-banner">
                  <Icon name="flag" size="1rem" className="rc-flag-banner-icon" />
                  <div className="rc-flag-banner-text">
                    <div className="rc-flag-banner-title">Needs Review</div>
                    <div className="rc-flag-banner-reason">
                      {editModal.draft.flag_reason || 'This content was flagged for review.'}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="rc-flag-banner-clear-btn"
                    onClick={handleClearFlag}
                    disabled={clearingFlag}
                  >
                    {clearingFlag ? 'Clearing…' : 'Clear Flag'}
                  </button>
                </div>
              )}
              {editModal.draft?.title && (
                <div className="rc-form-group">
                  <label>Tracking Title</label>
                  <div className="rc-tracking-title">{editModal.draft.title}</div>
                </div>
              )}
              {editModal.draft?.content_type !== 'social' && (
                <div className="rc-form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={editForm.headline}
                    onChange={(e) => setEditForm(prev => ({ ...prev, headline: e.target.value }))}
                    placeholder="Enter title..."
                    autoComplete="off"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                  />
                </div>
              )}
              <div className="rc-form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ margin: 0 }}>Body Content</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>
                      {editForm.body.trim().split(/\s+/).filter(w => w.length > 0).length} words
                    </span>
                    <button
                      type="button"
                      className="rc-btn-copy"
                      onClick={() => {
                        navigator.clipboard.writeText(editForm.body);
                        modal.toast('Body content copied to clipboard', 'success');
                      }}
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.8rem',
                        background: '#F3F4F6',
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      <Icon name="docs" size="0.875rem" />
                      Copy
                    </button>
                  </div>
                </div>
                <textarea
                  value={editForm.body}
                  onChange={(e) => setEditForm(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Enter content body..."
                  rows={8}
                />
              </div>
              <div className="rc-form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ margin: 0 }}>Hashtags</label>
                  <button
                    type="button"
                    className="rc-btn-copy"
                    onClick={() => {
                      const hashtagText = editForm.hashtags.map(h => h.startsWith('#') ? h : `#${h}`).join(' ');
                      navigator.clipboard.writeText(hashtagText);
                      modal.toast('Hashtags copied to clipboard', 'success');
                    }}
                    style={{
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.8rem',
                      background: '#F3F4F6',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <Icon name="docs" size="0.875rem" />
                    Copy
                  </button>
                </div>
                <input
                  type="text"
                  value={editForm.hashtags.map(h => h.startsWith('#') ? h : `#${h}`).join(' ')}
                  onChange={(e) => {
                    const text = e.target.value;
                    const hashtags = text.match(/#?\w+/g) || [];
                    setEditForm(prev => ({ ...prev, hashtags: hashtags.map(h => h.startsWith('#') ? h : `#${h}`) }));
                  }}
                  placeholder="Enter hashtags (e.g., #marketing #socialmedia)..."
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                />
              </div>
              {(() => {
                const draftSources = editModal.draft?.sources && editModal.draft.sources.length > 0
                  ? editModal.draft.sources
                  : ideaSourceFallback;
                if (!draftSources || draftSources.length === 0) return null;
                return (
                <div className="rc-form-group">
                  <label>{draftSources.length > 1 ? 'Sources' : 'Source'}</label>
                  <div className="rc-draft-sources">
                    {draftSources.map((source, idx) => {
                      const sourceKey = source._id || `inline-${idx}`;
                      const isActiveInViewer = sourceViewer.open && sourceViewer.source &&
                        (sourceViewer.source._id || `inline-${idx}`) === sourceKey;
                      return (
                        <div
                          key={sourceKey}
                          className={`rc-draft-source-item ${isActiveInViewer ? 'active' : ''}`}
                        >
                          <span
                            className="rc-draft-source-icon-badge"
                            title={getDraftSourceTypeName(source)}
                          >
                            <Icon name={getDraftSourceIcon(source.source_type)} size="0.8rem" />
                          </span>
                          <span className="rc-draft-source-name">{source.name || 'Untitled Source'}</span>
                          {source.created_at && (
                            <span className="rc-draft-source-date">{formatDate(source.created_at)}</span>
                          )}
                          <button
                            type="button"
                            className="rc-draft-source-view-btn"
                            onClick={() => viewDraftSource(source)}
                            title="View source"
                          >
                            <Icon name="eye" size="0.9rem" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })()}
              {editModal.draft?.idea_id && editModal.draft?.ideas_doc_id && (
                <div className="rc-form-group">
                  <label>Approved Idea</label>
                  <div className="rc-draft-sources">
                    <div className={`rc-draft-source-item ${ideaViewer.open ? 'active' : ''}`}>
                      <span className="rc-draft-source-icon-badge" title="Idea">
                        <Icon name="lightbulb" size="0.8rem" />
                      </span>
                      <span className="rc-draft-source-name">{editModal.draft.title || 'Approved Idea'}</span>
                      <button
                        type="button"
                        className="rc-draft-source-view-btn"
                        onClick={() => viewOriginalIdea(editModal.draft)}
                        title={ideaViewer.open ? 'Hide idea' : 'View idea'}
                      >
                        <Icon name="eye" size="0.9rem" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {editModal.draft?.media_url && (
                <div className="rc-form-group">
                  <label>Attached Media</label>
                  <div className="rc-media-preview">
                    {editModal.draft.media_url.match(/\.(mp4|webm|mov)$/i) ? (
                      <video
                        src={editModal.draft.media_url}
                        controls
                        className="rc-media-preview-video"
                      />
                    ) : (
                      <img
                        src={editModal.draft.media_url}
                        alt="Attached media"
                        className="rc-media-preview-img"
                      />
                    )}
                    {editModal.draft.media_filename && (
                      <p className="rc-media-filename">{editModal.draft.media_filename}</p>
                    )}
                  </div>
                </div>
              )}
              <div className="rc-modal-actions">
                <button className="rc-btn-cancel" onClick={closeEditModal}>
                  Cancel
                </button>
                <button className="rc-btn-save" onClick={handleSaveEdit} disabled={saving || isEditFormUnchanged()}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>

          {sourceViewer.open && (
            <div className="rc-modal-source-col">
              <div className="rc-source-pane-header">
                <div className="rc-source-pane-header-info">
                  <h4>{sourceViewer.source?.name || 'Loading source…'}</h4>
                  {sourceViewer.source && (
                    <div className="rc-source-pane-meta">
                      <span className="rc-source-pane-type-badge">
                        <Icon name={getDraftSourceIcon(sourceViewer.source.source_type)} size="0.75rem" />
                        {getDraftSourceTypeName(sourceViewer.source)}
                      </span>
                      {sourceViewer.source.created_at && (
                        <>
                          <span className="rc-source-pane-meta-divider">•</span>
                          <span>{formatDate(sourceViewer.source.created_at)}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="rc-source-pane-header-actions">
                  {sourceViewer.source?.content && (
                    <button
                      className={`rc-source-pane-copy-btn ${sourceViewer.copied ? 'copied' : ''}`}
                      onClick={copyDraftSourceContent}
                    >
                      <Icon name={sourceViewer.copied ? 'ok' : 'docs'} size="0.85rem" />
                      {sourceViewer.copied ? 'Copied!' : 'Copy'}
                    </button>
                  )}
                  <button className="modal-close-btn" onClick={closeSourceViewer} aria-label="Close source viewer" />
                </div>
              </div>
              <div className="rc-source-pane-body">
                {sourceViewer.loading ? (
                  <p className="rc-source-pane-empty">Loading source…</p>
                ) : !sourceViewer.source ? (
                  <p className="rc-source-pane-empty">Could not load this source.</p>
                ) : (
                  renderDraftSourceContent(sourceViewer.source)
                )}
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      {/* Recipient Selector Modal */}
      <RecipientSelectorModal
        isOpen={recipientModal.open}
        onClose={() => setRecipientModal({ open: false, brandId: null, draftIds: [] })}
        onConfirm={handleRecipientsSelected}
        itemType="content-drafts"
        itemId={recipientModal.draftIds[0]}
        itemCount={recipientModal.draftIds.length}
      />

      {/* Content Preview Modal */}
      {renderContentPreviewModal()}

      {/* Comment Modal */}
      <CommentModal
        isOpen={commentModalState.open}
        onClose={() => setCommentModalState({ open: false, draftId: null, comments: [] })}
        itemId={commentModalState.draftId}
        itemType="content-draft"
        comments={commentModalState.comments}
        user={user}
        onCommentAdded={(updatedComments) => {
          setDrafts(prevDrafts => prevDrafts.map(draft =>
            draft._id === commentModalState.draftId
              ? { ...draft, comments: updatedComments }
              : draft
          ));
        }}
        onCommentDeleted={(updatedComments) => {
          setDrafts(prevDrafts => prevDrafts.map(draft =>
            draft._id === commentModalState.draftId
              ? { ...draft, comments: updatedComments }
              : draft
          ));
        }}
        isProxyMode={isProxyMode}
      />

      <OperatorImageModal
        isOpen={operatorImageModal.open}
        onClose={() => setOperatorImageModal({ open: false, draft: null })}
        onConfirm={handleOperatorImageConfirm}
        existingMediaUrl={operatorImageModal.draft?.media_url}
        existingOriginalUrl={operatorImageModal.draft?.media_url_original}
        existingCropData={operatorImageModal.draft?.media_crop_data}
        existingBackgroundColor={operatorImageModal.draft?.background_color}
        brandName={operatorImageModal.draft?.brand?.name}
        brandLogo={operatorImageModal.draft?.brand?.icon_url}
        draftTitle={operatorImageModal.draft?.content_type === 'social' ? null : (operatorImageModal.draft?.headline || operatorImageModal.draft?.title)}
        draftBody={operatorImageModal.draft?.body}
        contentType={operatorImageModal.draft?.content_type}
        draftDate={formatDate(operatorImageModal.draft?.client_decision_at || operatorImageModal.draft?.created_at)}
        showVersions={false}
      />

    </div>
  );
};

export default ReviewContentPanel;
